import invariant from 'invariant';
import type { ElementType, ReactNode } from 'react';
import { useRef } from 'react';

import type {
  MultiInputExtraData,
  MultiInputRemoveButtonAlignment,
} from 'components/MultiInput/MultiInput';
import { MultiInput } from 'components/MultiInput/MultiInput';
import { type Spacing } from 'theme/theme';

import { Field } from './Field/Field';

type Props<TOption = string> = {
  name: string;
  component?: ElementType;
  label?: ReactNode;
  description?: string;
  matchGeneralError?: string;
  placeholder?: string;
  maxInputs: number;
  type?: string;
  disabled?: boolean;
  extraData?: MultiInputExtraData[];
  noMargin?: boolean;
  hideControls?: boolean;
  addText?: string;
  removeText?: string;
  showEmpty?: boolean;
  itemSpacing?: Spacing;
  newItemFactory?: (index: number) => TOption;
  removeButtonAlignment?: MultiInputRemoveButtonAlignment;
};

export function MultiInputField<TOption>({
  name,
  component,
  label,
  description,
  matchGeneralError,
  placeholder,
  maxInputs,
  noMargin,
  type = 'text',
  disabled,
  extraData,
  hideControls,
  addText,
  removeText,
  showEmpty,
  itemSpacing,
  newItemFactory,
  removeButtonAlignment,
}: Props<TOption>) {
  // Render this field's errors closer to the input using a React portal
  const errorRef = useRef<HTMLDivElement>(null);

  return (
    <Field
      name={name}
      label={label}
      errorRef={errorRef}
      description={description}
      matchGeneralError={matchGeneralError}
      noMargin={noMargin}
      renderInput={({ value, id, qaId, updateValueWith }) => {
        invariant(
          Array.isArray(value),
          `Invalid value for MultiInputField named ${name}: ${value}`,
        );
        return (
          <MultiInput
            id={id}
            component={component}
            name={name}
            type={type}
            values={value}
            errorRef={errorRef}
            maxInputs={maxInputs}
            placeholder={placeholder}
            itemSpacing={itemSpacing}
            onChange={updateValueWith}
            disabled={disabled}
            extraData={extraData}
            hideControls={hideControls}
            addText={addText}
            removeText={removeText}
            showEmpty={showEmpty}
            qaId={qaId}
            newItemFactory={newItemFactory}
            removeButtonAlignment={removeButtonAlignment}
          />
        );
      }}
    />
  );
}
